import * as React from 'react';

import { EditIcon } from '../../../assets/icons/general/edit-4';
import { cn } from '../../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';

import { editableSelectVariants, editableSelectIconVariants } from './variants';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface EditableSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  defaultEditable?: boolean;
}

const EditableSelect = React.forwardRef<HTMLButtonElement, EditableSelectProps>(
  (
    {
      value = '',
      onChange,
      onEditStart,
      onEditEnd,
      options,
      placeholder = 'Select an option',
      disabled = false,
      className,
      defaultEditable = false,
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = React.useState(defaultEditable);
    const [internalValue, setInternalValue] = React.useState(value);

    React.useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleEditClick = () => {
      if (disabled) return;
      setIsEditing(true);
      onEditStart?.();
    };

    const handleValueChange = (newValue: string) => {
      setInternalValue(newValue);
      onChange?.(newValue);
      // Close the select after choosing
      setTimeout(() => {
        setIsEditing(false);
        onEditEnd?.();
      }, 150);
    };

    const handleBlur = () => {
      setIsEditing(false);
      onEditEnd?.();
    };

    const selectedOption = options.find((opt) => opt.value === internalValue);
    const displayValue = selectedOption?.label || placeholder;

    if (isEditing) {
      return (
        <Select
          value={internalValue}
          onValueChange={handleValueChange}
          disabled={disabled}
          onOpenChange={(open) => {
            if (!open) {
              handleBlur();
            }
          }}
        >
          <SelectTrigger ref={ref} className={className} autoFocus>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <div
        className={cn(
          editableSelectVariants({ mode: 'view', disabled }),
          className,
        )}
        onClick={handleEditClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEditClick();
          }
        }}
      >
        <span
          className={cn(
            'flex-1 truncate',
            !internalValue && 'text-muted-foreground',
          )}
        >
          {displayValue}
        </span>
        <EditIcon
          className={cn(
            'h-4 w-4',
            editableSelectIconVariants({ mode: 'view', disabled }),
          )}
        />
      </div>
    );
  },
);

EditableSelect.displayName = 'EditableSelect';

export { EditableSelect };
